import Grid from "@mui/material/Grid2";
import { isEmpty } from "lodash";
import dayjs from "dayjs";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { matchSorter } from "match-sorter";
import { SyntheticEvent, useEffect, useState } from "react";

import { TabSessionStorageResponse } from "../../../models/api/TabSessionStorage";
import { TabData } from "../../../models/TabSessionStorage";
import { ListOptions } from "../../../models/Master";
import {
  BenefitPlanDetails,
  EligibilityCheck,
  EligibilityInformation,
  GetPatient,
  GetPatientInformationPayload,
  LogContractIdPbpPayload,
  MemberInfoData,
  Patient,
  PatientInformation,
  PatientInformationView,
  SaveInformation,
} from "../../../models/Patient";
import { UserPageTrack } from "../../../models/User";
import {
  displayFlex,
  flexAllCentre,
  flexAllEnd,
  flexColumn,
  flexRow,
} from "../../../styles/mui/styles/display";
import { ButtonVerifyStyle, MfaModalBox, PatientDetailsContainer } from "../../../app/styles/styles";
import fontWeight from "../../../styles/mui/fontWeight";
import colors from "../../../styles/colors/colors";
import { FormInputText } from "../../../components/FormPriorAuthReAuth/FormInputText";
import { rules } from "../../../utils/validation/Validation";
import {
  eligibilityFormInitialState,
  getType,
  HealthPlanData,
  NO_RECORD_FOUND,
} from "../../../constants/eligibility";
import DatePicker from "../../../components/FormPriorAuthReAuth/DatePicker";
import {
  AutoComplete,
  ButtonMinWidth,
  ButtonMinWidth_Outlined,
  StyledPopperCheckBoxAutocomplete,
  verticalScrollStyle,
} from "../styles/style";
import useMenuItem from "../../../hooks/useMenuItem";
import {
  getCurrnetPageFullPath,
  getHealthPlanValueType,
  initializeLanguageLookup,
  getUaserPageViewPayload,
  getValue,
  isEligible,
  isOptionEqualToValue,
} from "../../../utils";
import { ApplicationTitles } from "../../../constants/HomePage";
import { getValidMessageType } from "../../../constants/User";
import { redirectUri } from "../../../constants/Config";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import {
  CreatePatientActionDispatchTypes,
  EligibilityCheckActionDispatchTypes,
  InformationActionDispatchTypes,
  LogContractIdPBPDetailsActionDispatchTypes,
  MemberInfoDataActionDispatchTypes,
  PatientFromMbiActionDispatchTypes,
  PatientInformationByIdActionDispatchTypes,
  TextConstants,
} from "../../../constants/Patient";
import { EligibilityCheckResponse } from "../../../models/api/Patient";
import { EligibilityCheckFail, EligibilityCheckSuccess } from "./MemberInfo";
import { Numbers, SlotsDispatchTypes } from "../../../constants/Referral";
import { initialState } from "../../../reducers/PostCheckEligibility";
import { MyAccountModel } from "../../../models/MyAccount";
import PatientHealthPlanVerbiage from "./PatientHealthPlanVerbiage";
import { fontSize } from "../../../styles/mui/styles/font";
import { TabSessionStorageDispatchTypes } from "../../../constants/TabSessionStorage";
import { ButtonContained } from "../../../app/common/button";
import { HorizontalDivider } from "../../../styles/mui/styles/border";
import { Language } from "../../../models/MemberDetails";

export interface PropsFromState {
  healthPlanState: ListOptions[];
  TabSessionStorage: TabSessionStorageResponse;
  checkEligibilityData: EligibilityCheckResponse;
  patientFromMbiState: Patient;
  benefitPlanUidState: BenefitPlanDetails;
  loggedInUserState: MyAccountModel;
  patientInformationByIdState: PatientInformation;
  createPatientState: PatientInformationView;
  memberInfoDataState: MemberInfoData;
  informationState: PatientInformationView;
  createPatientErrorState: any;
  informationErrorState: any;
  logContractIdPBPDetailsState: boolean;
  masterLanguageState: Language[];
}

export interface PropsFromDispatch {
  resetStates: (actionType: string, field?: string) => void;
  getMasterHealthPlan: () => void;
  checkEligibility: (payload: EligibilityCheck) => void;
  postUserPageTrack: (payload: UserPageTrack) => void;
  saveTabSessionStorage: (sessionData: TabData) => void;
  saveEligibilityInformation: (sessionData: EligibilityInformation) => void;
  getHealthPlanDetailsByBenefitPlanUid: (benefitPlanUid: string) => void;
  getPatientFromMBI: (payload: GetPatient) => void;
  createPatient: (payload: FormData) => void;
  getPatientInformationById: (payload: GetPatientInformationPayload) => void;
  information: (payload: SaveInformation) => void;
  saveMemberInfo: (payload: MemberInfoData) => void;
  updateLogContractIdPbp: (payload: LogContractIdPbpPayload) => void;
  getLanguageMaster: () => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const PriorAuthWorkFlow: React.FC<AllProps> = ({
  healthPlanState,
  getMasterHealthPlan,
  postUserPageTrack,
  checkEligibility,
  checkEligibilityData,
  resetStates,
  getHealthPlanDetailsByBenefitPlanUid,
  getPatientFromMBI,
  patientFromMbiState,
  benefitPlanUidState,
  loggedInUserState,
  createPatient,
  patientInformationByIdState,
  createPatientState,
  getPatientInformationById,
  information,
  saveMemberInfo,
  memberInfoDataState,
  informationState,
  informationErrorState,
  createPatientErrorState,
  updateLogContractIdPbp,
  logContractIdPBPDetailsState,
  getLanguageMaster,
  masterLanguageState,
}: AllProps) => {
  const [healthPlan, setHealthPlan] = useState<ListOptions | null>(
    HealthPlanData
  );
  const [openEligibilityCheck, setOpenEligibilityCheck] = useState(false);
  const [eligibilityPayload, setEligibilityPayload] = useState<
    EligibilityCheck | null | undefined
  >(null);
  const { open, onOpen, onClose } = useMenuItem();
  const [warningType, setWarningType] = useState<number>(0);
  const [eligibilityData, setEligibilityData] =
    useState<EligibilityCheckResponse>(initialState);
  const [contractIdPbpAvailable, setContractIdPbpAvailable] = useState<boolean>(false);
  const methods = useForm<any>({
    defaultValues: eligibilityFormInitialState,
    mode: "all",
  });

  const { control, setValue, clearErrors, getValues, handleSubmit, reset } =
    methods;
  const values = getValues();

  const patientFromMbi = () => {
    const eligibilityResponseMbi = getValue(
      checkEligibilityData,
      "response.supplementalInformation.info[0]",
    );
    const eligibilityResponselastName = getValue(
      checkEligibilityData,
      "response.record.subscriberName",
    );
    setTimeout(() => {
      getPatientFromMBI({
        mbi: isEmpty(getValue(eligibilityResponseMbi, "mbi", null))
          ? null
          : getValue(eligibilityResponseMbi, "mbi"),
        dob: dayjs(getValue(eligibilityPayload, "dob")).format(
          DateFormats.YYYY_MM_DD,
        ),
        lastName: getValue(
          eligibilityResponselastName,
          "last",
          eligibilityPayload?.lastName,
        ),
        subscriberId: getValue(eligibilityPayload, "policyId"),
      });
    }, 100);
  };
  const onHealthPlanSelection = (
    _e: SyntheticEvent<Element, Event>,
    value: any
  ) => {
    onClose();
    setHealthPlan(value);
    setValue("healthPlan", value.value);
    setWarningType(getType(value));
    clearErrors("healthPlan");
  };
  const handleKeyDown = (event: any) => {
    if (event.key === " " && !event.target.value.trim()) {
      event.preventDefault();
    }
  };
  const handleCheckEligibility = () => {
    resetAPIResponse();
    setTimeout(() => {
      setOpenEligibilityCheck(false);
      const payload = {
        lastName: getValues("lastName"),
        dob: formatDate(getValues("dateOfBirth"), DateFormats.YYYY_MM_DD),
        policyId: getValues("subscriberId"),
        payerId: getValues("healthPlan")
          ? getHealthPlanValueType(getValues("healthPlan"), 0)
          : "",
        payerName: getValues("healthPlan")
          ? getHealthPlanValueType(getValues("healthPlan"), 1)
          : "",
      };
      setEligibilityPayload(payload);
      getLanguageMaster();
      checkEligibility(payload);
    }, 0);
  };
  const handleReset = () => {
    setOpenEligibilityCheck(false);
    setHealthPlan(null);
    setWarningType(0);
    setEligibilityData(initialState);
    resetAPIResponse();
    reset({ values, keepDefaultValue: true });
  };
  const resetAPIResponse = () => {
    [
      EligibilityCheckActionDispatchTypes.ELIGIBILITY_CHECK_RESET,
      PatientFromMbiActionDispatchTypes.PATIENT_FROM_MBI_RESET,
      PatientInformationByIdActionDispatchTypes.PATIENT_INFORMATION_BY_ID_RESET,
      InformationActionDispatchTypes.INFORMATION_RESET,
      CreatePatientActionDispatchTypes.CREATE_PATIENT_RESET,
      TabSessionStorageDispatchTypes.SET_TAB_DATA_RESET,
      MemberInfoDataActionDispatchTypes.MEMBER_INFO_DATA_RESET,
      SlotsDispatchTypes.SLOTS_RESET
    ].forEach((actionType) => resetStates(actionType));
  };
  useEffect(() => {
    handleReset();
    getMasterHealthPlan();
    postUserPageTrack(
      getUaserPageViewPayload(
        getValidMessageType(ApplicationTitles.CREATE_PRIOR_AUTH),
        getCurrnetPageFullPath(),
        redirectUri,
        ""
      )
    );
  }, []);

  useEffect(() => {
    initializeLanguageLookup(masterLanguageState);
  }, [masterLanguageState]);

  useEffect(() => {
    if (
      checkEligibilityData &&
      getValue(
        checkEligibilityData,
        "response.additionalNotes.eligibilityState"
      ) > Numbers.ZERO
    ) {
      setEligibilityData(checkEligibilityData);
      setOpenEligibilityCheck(true);
      if (
        !isEmpty(
          getValue(
            checkEligibilityData,
            "response.additionalNotes.benefitPlanUid"
          )
        )
      ) {
        getHealthPlanDetailsByBenefitPlanUid(
          getValue(
            checkEligibilityData,
            "response.additionalNotes.benefitPlanUid"
          )
        );
      }
      if (isEmpty(getValue(eligibilityPayload, "dob")) || getValue(eligibilityPayload, "dob") === null) {
        return;
      }
      if (
        !isEmpty(
          getValue(checkEligibilityData, "response.record.subscriberName.last")
        )
      ) {
        patientFromMbi();
      }
      return;
    }
    setOpenEligibilityCheck(false);
  }, [checkEligibilityData]);

  useEffect(() => {
    if (
      patientInformationByIdState &&
      getValue(patientInformationByIdState, "isAuthNotRequired") &&
      logContractIdPBPDetailsState
    ) {
      setContractIdPbpAvailable(true);
    }
  }, [logContractIdPBPDetailsState]);

  const handleConfirm = () => {
    resetStates(LogContractIdPBPDetailsActionDispatchTypes.LOG_CONTRACT_ID_PBP_DETAILS_RESET)
    setContractIdPbpAvailable(false);
  }

  return (
    <Box sx={verticalScrollStyle}>
      <Grid container spacing={2} mt={4} sx={PatientDetailsContainer}>
        <Grid size={{ xs: 12 }} sx={flexColumn}>
          <Grid container spacing={2} sx={flexRow} p={2}>
            <Grid size={{ xs: 12 }} sx={flexColumn}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid>
                  <Typography variant="h5" fontWeight={fontWeight[5]}>
                    REFERRAL DETAILS
                  </Typography>
                </Grid>
                <Grid>
                  <Typography
                    pt={1}
                    variant="body1"
                    fontWeight={fontWeight[4]}
                    color={colors.grey[2]}
                    textAlign="right"
                  >
                    NOTE: Authorizations will only be backdated up to 72 hours
                    from date of receipt.
                  </Typography>
                </Grid>
              </Grid>
              <Typography
                pt={1}
                variant="body1"
                fontWeight={fontWeight[4]}
                color={colors.grey[2]}
              >
                Please check for eligiblity before filling out prior
                authorization form
              </Typography>
            </Grid>
            <Grid size={{ xs: 4 }}></Grid>
          </Grid>
          <Divider sx={{ mx: 2 }} />
          <Grid>
            <Grid container spacing={2} sx={flexRow} p={2}>
              <Grid size={{ xs: 2 }}>
                <FormInputText
                  name="lastName"
                  label="Last Name:"
                  control={control}
                  helper={rules.eligibilityLastNameTextRegex}
                />
              </Grid>
              <Grid>
                <FormControl variant="standard">
                  <DatePicker
                    name="dateOfBirth"
                    control={control}
                    label={"Date of Birth:"}
                    helper={rules.dateOfBirthRequired}
                    allowFutureDate={false}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 2 }}>
                <FormInputText
                  name="subscriberId"
                  label="Subscriber ID:"
                  control={control}
                  helper={rules.subscriberId}
                />
              </Grid>
              <Grid size={{ xs: 2 }}>
                <Box sx={displayFlex}>
                  <Typography
                    variant="body2"
                    fontWeight={fontWeight[4]}
                    color={colors.fonts[2]}
                  >
                    Health Plan:
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={fontWeight[2]}
                    color={colors.red[103]}
                    pl={"0.25rem"}
                  >
                    *
                  </Typography>
                </Box>
                <Controller
                  render={({ fieldState: { error } }) => (
                    <>
                      <Autocomplete
                        sx={AutoComplete}
                        options={healthPlanState}
                        freeSolo
                        forcePopupIcon
                        getOptionLabel={(option: string | ListOptions) =>
                          typeof option === "string" ? option : option.label
                        }
                        open={open}
                        onOpen={() => onOpen()}
                        onClose={() => onClose()}
                        PopperComponent={StyledPopperCheckBoxAutocomplete}
                        filterOptions={(options: any, { inputValue }) =>
                          matchSorter(options, inputValue, {
                            keys: ["label"],
                          })
                        }
                        isOptionEqualToValue={(option, value) =>
                          isOptionEqualToValue(option, value)
                        }
                        id="auto-complete"
                        autoComplete
                        disableClearable
                        clearIcon={""}
                        onChange={onHealthPlanSelection}
                        includeInputInList
                        value={healthPlan ?? undefined}
                        noOptionsText={NO_RECORD_FOUND}
                        data-testid="health-plan"
                        renderInput={(params: any) => {
                          params.inputProps.maxLength = 50;
                          return (
                            <Box>
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder="Choose"
                                onKeyDown={handleKeyDown}
                                inputProps={{
                                  ...params.inputProps,
                                  maxLength: 50,
                                  style: { fontSize: fontSize[13] },
                                }}
                              />
                            </Box>
                          );
                        }}
                        renderOption={(props, option) => {
                          const { key, ...Itemprops } = props;
                          return (
                            <Typography
                              key={key}
                              {...Itemprops}
                              variant="body1"
                              fontWeight={fontWeight[3]}
                              color={colors.fonts[25]}
                            >
                              {option.label}
                            </Typography>
                          );
                        }}
                      />
                      {error && (
                        <FormHelperText>{error.message}</FormHelperText>
                      )}
                    </>
                  )}
                  control={control}
                  name="healthPlan"
                  rules={rules.checkEligibilityHealthPlan}
                />
              </Grid>
              <Grid mt={1}>
                <Button
                  variant="contained"
                  onClick={handleSubmit(handleCheckEligibility)}
                  sx={ButtonMinWidth}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={ButtonMinWidth_Outlined}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {
        <PatientHealthPlanVerbiage
          warningType={warningType}
          openEligibilityCheck={openEligibilityCheck}
          eligibilityData={eligibilityData}
        />
      }
      {openEligibilityCheck && (
        <Grid container spacing={2} mt={1} sx={PatientDetailsContainer}>
          {eligibilityData &&
            isEligible(getValue(eligibilityData, "response")) === true && (
              <EligibilityCheckSuccess
                eligibilityResponse={eligibilityData}
                eligibilityPayload={eligibilityPayload}
                patientFromMbiState={patientFromMbiState}
                benefitPlanUidState={benefitPlanUidState}
                loggedInUserState={loggedInUserState}
                createPatient={createPatient}
                patientInformationByIdState={patientInformationByIdState}
                createPatientState={createPatientState}
                getPatientInformationById={getPatientInformationById}
                information={information}
                saveMemberInfo={saveMemberInfo}
                memberInfoDataState={memberInfoDataState}
                informationState={informationState}
                createPatientErrorState={createPatientErrorState}
                informationErrorState={informationErrorState}
                resetAPIResponse={resetAPIResponse}
                updateLogContractIdPbp={updateLogContractIdPbp}
                logContractIdPBPDetailsState={logContractIdPBPDetailsState}
                masterLanguageState={masterLanguageState}
              />
            )}
          {eligibilityData &&
            isEligible(getValue(eligibilityData, "response")) === false && (
              <EligibilityCheckFail
                eligibilityData={eligibilityData}
                eligibilityPayload={eligibilityPayload}
                memberInfoDataState={memberInfoDataState}
                saveMemberInfo={saveMemberInfo}
                resetAPIResponse={resetAPIResponse}
                masterLanguageState={masterLanguageState}
              />
            )}
          {contractIdPbpAvailable && (
            <Modal
              open={contractIdPbpAvailable}
              onClose={() => {}}
              aria-labelledby="session-timeout-modal"
              aria-describedby="session-timeout-modal-description"
              sx={flexAllCentre}
            >
              <Box sx={MfaModalBox}>
                <Grid container mb="0.4rem">
                  <Grid size={{ xs: 11.5 }}>
                    <Typography id="modal-modal-title" variant="h2">
                      {TextConstants.CONTRACTID_PBP_MESSAGE}
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={HorizontalDivider} />
                <Grid container>
                  <Grid size={{ xs: 12 }} sx={flexAllEnd}>
                    <ButtonContained
                      id="btnVerify"
                      onClick={handleConfirm}
                      label="Confirm"
                      style={ButtonVerifyStyle}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Modal>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default PriorAuthWorkFlow;
